import React from "react";

import Link from "next/link";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
} from "../Navigation";
import { ChevronDown } from "lucide-react";

import { Menu, MenuItem as DBMenuItem } from "@/types/navigation";

interface MenuItem {
  title: string;
  path: string;
  children?: MenuItem[];
}

interface MenusProps {
  menu?: MenuItem[];
  customMenus?: Menu[];
  basePath?: string;
}

// Simple Tree Builder for Storefront
function buildMenuTree(items: DBMenuItem[]): MenuItem[] {
  const map = new Map<string, MenuItem & { id: string }>();
  const roots: (MenuItem & { id: string })[] = [];

  // 1. Create nodes
  items.forEach(item => {
    map.set(item.id, {
      id: item.id,
      title: item.title,
      path: item.url || '#',
      children: []
    });
  });

  // 2. Connect tree
  items.forEach(item => {
    const node = map.get(item.id)!;
    if (item.parent_id && map.has(item.parent_id)) {
      map.get(item.parent_id)!.children!.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

export function Menus({ menu = [], customMenus = [], basePath = '' }: MenusProps) {
  const [activeMenu, setActiveMenu] = React.useState<string | null>(null);
  let timeoutId: NodeJS.Timeout;

  const handleMouseEnter = (title: string) => {
    if (timeoutId) clearTimeout(timeoutId);
    setActiveMenu(title);
  };

  const handleMouseLeave = () => {
    timeoutId = setTimeout(() => {
      setActiveMenu(null);
    }, 100);
  };

  // Resolve Menus
  let navMenu: MenuItem[] = [];

  if (customMenus && customMenus.length > 0) {
    if (customMenus.length === 1) {
      // If only one menu is selected, unwrap it (show its items directly)
      navMenu = buildMenuTree(customMenus[0]?.items || []);
    } else {
      // If multiple menus, wrap each in a dropdown titled with the menu name
      navMenu = customMenus.map(cMenu => ({
        title: cMenu.title,
        path: '#', // TBD: Can a menu have a main link? For now '#'
        children: buildMenuTree(cMenu.items || [])
      }));
    }
  } else if (menu.length > 0) {
    navMenu = menu;
  } else {
    // Default fallback: No dummy links as per user request
    navMenu = [];
  }

  if (navMenu.length === 0) {
    return null; // Don't render empty nav
  }

  const resolvePath = (path: string) => {
    if (!path) return '#';
    if (path.startsWith('http')) return path;

    const cleanPath = path.startsWith('/') ? path : `/${path}`;

    // Use passed basePath (e.g., /store/[slug])
    if (basePath) {
      // Avoid double slashes if basePath ends with /
      const normalizedBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
      if (!cleanPath.startsWith(normalizedBasePath)) {
        return `${normalizedBasePath}${cleanPath}`;
      }
    }
    return cleanPath;
  };

  return (
    <NavigationMenu>
      <NavigationMenuList className="gap-2">
        {navMenu.map((item) => (
          <NavigationMenuItem
            key={item.title}
            onMouseEnter={() => handleMouseEnter(item.title)}
            onMouseLeave={handleMouseLeave}
            className="group"
          >
            {item.children && item.children.length > 0 ? (
              <>
                <Link
                  href={resolvePath(item.path)}
                  className={`inline-flex h-10 items-center justify-center px-4 py-2 text-xs font-medium transition-colors cursor-pointer group pointer-events-auto relative z-50 rounded-md bg-transparent text-primary-foreground hover:bg-primary-foreground/10 focus:bg-primary-foreground/10 focus:text-primary-foreground data-[state=open]:bg-primary-foreground/10 data-[active]:bg-primary-foreground/10`}
                >
                  {item.title}
                  <ChevronDown
                    className={`ml-1 h-3 w-3 transition-transform duration-200 group-hover:rotate-180 ${activeMenu === item.title ? 'rotate-180' : ''}`}
                  />
                </Link>
                <NavigationMenuContent isOpen={activeMenu === item.title}>
                  <ul className="grid w-full gap-3 md:grid-cols-4 lg:w-full bg-transparent">
                    {item.children.map((child) => (
                      <li key={child.title}>
                        <Link
                          href={resolvePath(child.path)}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary-foreground/10 text-primary-foreground focus:bg-primary-foreground/10"
                        >
                          <div className="text-sm font-medium leading-none">{child.title}</div>
                          <p className="line-clamp-2 text-sm leading-snug text-primary-foreground/70">
                            {/* Optional description if we had it */}
                            تصفح قسم {child.title}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </>
            ) : (
              <Link
                href={resolvePath(item.path)}
                className="inline-flex h-10 items-center justify-center px-4 py-2 text-xs font-medium transition-colors cursor-pointer group pointer-events-auto rounded-md bg-transparent text-primary-foreground hover:bg-primary-foreground/10 focus:bg-primary-foreground/10 focus:text-primary-foreground data-[state=open]:bg-primary-foreground/10 data-[active]:bg-primary-foreground/10"
              >
                {item.title}
              </Link>
            )}
          </NavigationMenuItem>
        ))
        }
      </NavigationMenuList >
    </NavigationMenu >
  );
}
